import React, { ReactNode } from "react";
import { Alert, Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { BookHalf } from "react-bootstrap-icons";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";

import { getAnalyticsWidget, getGenres, createExportSchedule } from "../../../utils/Requests";
import { getAnalyticsBrandLabel, getDefaultDateRange, getWidgetTheme, replaceAnalyticsBrandTerminology } from "../../../utils/analytics";
import { AnalyticsFilters, AnalyticsFilterOptions, DocsSidebarEntry } from "../../../types/analytics";
import AnalyticsFiltersComponent from "../../../components/analytics/AnalyticsFilters";
import MetricCards, { MetricCardItem } from "../../../components/analytics/MetricCards";
import { DocsSidebar, ExportButton, ScheduleExportModal, WidgetCard, PopPeriodBanner } from "../../../components/analytics";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import Logger from "../../../utils/Logger";

type ContentGenreExplorerProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type ContentGenreExplorerState = {
    isGenresLoading: boolean;
    isLoading: boolean;
    error: string | null;
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    genres: string[];
    selectedGenre: string;
    metricsData: Record<string, any>[];
    topBrandsData: TopBrandPoint[];
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
    showScheduleModal: boolean;
    editingSchedule: null;
};

type TopBrandPoint = {
    thingBrandName: string;
    plays: number;
};

class ContentGenreExplorer extends React.Component<ContentGenreExplorerProps, ContentGenreExplorerState> {
    constructor(props: ContentGenreExplorerProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();
        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandId: [],
            devicePlatform: []
        };

        this.state = {
            isGenresLoading: true,
            isLoading: false,
            error: null,
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandIds: [],
                devicePlatforms: []
            },
            genres: [],
            selectedGenre: "",
            metricsData: [],
            topBrandsData: [],
            sidebarOpen: false,
            sidebarEntries: [],
            showScheduleModal: false,
            editingSchedule: null
        };
    }

    componentDidMount(): void {
        this.initializePage();
    }

    private initializePage = async (): Promise<void> => {
        try {
            this.setState({ isGenresLoading: true, error: null });

            const genreResponse = await getGenres(this.props.customerConfig);
            const genreBody = await genreResponse.json();
            const rawGenres: unknown = genreBody?.items?.[0]?.genres;
            const genreOptions = Array.isArray(rawGenres)
                ? rawGenres
                    .filter((genre): genre is string => typeof genre === "string")
                : [];

            const normalizedGenres = [...genreOptions];
            if (!normalizedGenres.includes("All Genres")) {
                normalizedGenres.unshift("All Genres");
            }

            const defaultGenre = "All Genres";

            this.setState({
                genres: normalizedGenres,
                selectedGenre: defaultGenre,
                isGenresLoading: false
            }, () => {
                if (defaultGenre) {
                    this.fetchExplorerData(defaultGenre);
                }
            });
        } catch (error) {
            Logger.error("Error initializing content genre explorer", error);
            this.setState({
                error: "Failed to load content genre explorer",
                isGenresLoading: false
            });
        }
    };

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private getExportData = (): ExportableData[] => {
        const { metricsData, topBrandsData, selectedGenre, appliedFilters } = this.state;
        const data: ExportableData[] = [];

        if (metricsData.length > 0) {
            const row = metricsData[0];
            const metricRows = Object.keys(row)
                .filter(k => !k.endsWith("_percent_change"))
                .map(k => ({ metric: k, value: row[k] }));
            data.push({
                title: `Genre Metrics - ${selectedGenre}`,
                columns: [
                    { key: "metric", label: "Metric" },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: metricRows,
                metadata: { filters: appliedFilters, pageKey: "content_genre_explorer", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (topBrandsData.length > 0) {
            data.push({
                title: `${replaceAnalyticsBrandTerminology("Top Brands", this.props.customerConfig)} - ${selectedGenre}`,
                columns: [
                    { key: "thingBrandName", label: getAnalyticsBrandLabel(this.props.customerConfig) },
                    { key: "plays", label: "Plays", format: "number" }
                ],
                rows: topBrandsData,
                metadata: { filters: appliedFilters, pageKey: "content_genre_explorer", customerSlug: this.props.customerConfig.slug }
            });
        }

        return data;
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "Genre Explorer",
            pageKey: "content_genre_explorer",
            customerSlug: this.props.customerConfig.slug,
            filters: this.state.appliedFilters,
            getData: this.getExportData,
            getContainerRef: () => null
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters }
        }), () => {
            const { selectedGenre } = this.state;
            if (selectedGenre) {
                this.fetchExplorerData(selectedGenre);
            }
        });
    };

    private handleGenreChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedGenre = event.target.value;
        this.setState({ selectedGenre }, () => {
            if (selectedGenre) {
                this.fetchExplorerData(selectedGenre);
            } else {
                this.setState({ metricsData: [], topBrandsData: [] });
            }
        });
    };

    private fetchExplorerData = async (selectedGenre: string): Promise<void> => {
        const { appliedFilters } = this.state;

        try {
            this.setState({ isLoading: true, error: null });

            const [metricsResponse, topBrandsResponse] = await Promise.all([
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "content_genre_explorer_metrics",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.devicePlatform,
                    undefined,
                    undefined,
                    selectedGenre === "All Genres" ? undefined : { genre: selectedGenre }
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "content_genre_explorer_top_brands",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.devicePlatform,
                    undefined,
                    undefined,
                    selectedGenre === "All Genres" ? undefined : { genre: selectedGenre }
                )
            ]);

            const metricsResult = (metricsResponse as any)?.result;
            const metricsData = Array.isArray(metricsResult) ? metricsResult : [];

            const topBrandsRaw = (topBrandsResponse as any)?.result;
            const topBrandsData = (Array.isArray(topBrandsRaw) ? topBrandsRaw : [])
                .map((row: Record<string, any>) => ({
                    thingBrandName: String(row?.thing_brand_name || row?.thing_brandname || "Unknown"),
                    plays: this.toNumberOrNull(row?.plays) ?? 0
                }))
                .sort((a: TopBrandPoint, b: TopBrandPoint) => b.plays - a.plays)
                .slice(0, 25);

            this.setState({ metricsData, topBrandsData });

            /* eslint-disable camelcase */
            const widgetResponses: Array<{ key: string; response: unknown }> = [
                { key: "content_genre_explorer_metrics", response: metricsResponse },
                { key: "content_genre_explorer_top_brands", response: topBrandsResponse }
            ];
            const sidebarEntries: DocsSidebarEntry[] = widgetResponses
                .map(({ key, response: r }) => {
                    const meta = (r as any)?.widget;
                    if (!meta) return null;
                    const entry: DocsSidebarEntry = {
                        widgetKey: key,
                        displayName: meta.display_name,
                        description: meta.description,
                        schema: (r as any)?.schema
                    };
                    return entry;
                })
                .filter((e): e is DocsSidebarEntry => e !== null);
            /* eslint-enable camelcase */
            this.setState({ sidebarEntries });
        } catch (error) {
            Logger.error("Error fetching content genre explorer metrics", error);
            this.setState({ error: "Failed to load content genre explorer metrics" });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    private toTitleCase = (value: string): string => {
        return value
            .replace(/_/g, " ")
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    private toNumberOrNull = (value: unknown): number | null => {
        if (typeof value === "number" && Number.isFinite(value)) {
            return value;
        }
        if (typeof value === "string") {
            const parsed = Number(value);
            if (Number.isFinite(parsed)) {
                return parsed;
            }
        }
        return null;
    };

    private getMetricCards = (): MetricCardItem[] => {
        const { metricsData, sidebarEntries } = this.state;
        if (!Array.isArray(metricsData) || metricsData.length === 0) {
            return [];
        }

        const schema = sidebarEntries.find(e => e.widgetKey === "content_genre_explorer_metrics")?.schema as Record<string, { description?: string }> | undefined;
        const sourceRow = metricsData[0] as Record<string, any>;
        const ignoredKeys = new Set([
            "genre",
            "genre_name",
            "genreName",
            "date",
            "datepartition",
            "brand_count"
        ]);

        const candidateKeys = Object.keys(sourceRow)
            .filter(key => !ignoredKeys.has(key))
            .filter(key => !key.startsWith("previous_"))
            .filter(key => !key.endsWith("_diff"))
            .filter(key => !key.endsWith("_pop_Pct"))
            .filter(key => !key.endsWith("_pop_pct"))
            .filter(key => !key.endsWith("_percent_change"))
            .filter(key => !key.endsWith("_change"))
            .filter(key => this.toNumberOrNull(sourceRow[key]) !== null)
            .slice(0, 8);

        return candidateKeys.map((key): MetricCardItem => {
            const value = this.toNumberOrNull(sourceRow[key]);
            const percentChange = this.toNumberOrNull(sourceRow[`${key}_pop_Pct`])
                ?? this.toNumberOrNull(sourceRow[`${key}_pop_pct`])
                ?? this.toNumberOrNull(sourceRow[`${key}_percent_change`])
                ?? this.toNumberOrNull(sourceRow[`percent_change_${key}`]);
            const explicitNumericChange = this.toNumberOrNull(sourceRow[`${key}_diff`])
                ?? this.toNumberOrNull(sourceRow[`${key}_change`]);
            const previousValue = this.toNumberOrNull(sourceRow[`previous_${key}`]);
            const numericChange = explicitNumericChange
                ?? (value !== null && previousValue !== null ? value - previousValue : null);

            const isPercentMetric = key.includes("percent") || key.includes("rate");
            const isDecimalMetric = key.includes("per_") && !isPercentMetric;

            return {
                key,
                label: this.toTitleCase(key),
                value,
                changePercent: percentChange,
                numericChange,
                valueFormat: isPercentMetric ? "percent" : isDecimalMetric ? "decimal" : "number",
                description: schema?.[key]?.description
            };
        });
    };

    private getBrandBarColor = (index: number, total: number): string => {
        if (total <= 1) {
            return "#007bff";
        }

        const startHue = 205; // blue
        const endHue = 285; // purple
        const hue = startHue + ((endHue - startHue) * index) / (total - 1);

        return `hsl(${hue}, 78%, 52%)`;
    };

    public render(): ReactNode {
        const { filters, filterOptions, appliedFilters, genres, selectedGenre, isGenresLoading, isLoading, error, topBrandsData, sidebarOpen, sidebarEntries } = this.state;
        const metricCards = this.getMetricCards();
        const theme = getWidgetTheme(true);

        if (isGenresLoading) {
            return (
                <Container className="mw-100" key="genre-explorer" style={{ paddingBottom: "40px" }}>
                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2>Genre Explorer</h2>
                        </Col>
                    </Row>
                    <Row className="justify-content-center">
                        <Col className="text-center" style={{ padding: "32px" }}>
                            <Spinner animation="border" variant="primary" />
                        </Col>
                    </Row>
                </Container>
            );
        }

        return (
            <div style={{ display: "flex", alignItems: "flex-start" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
            <Container className="mw-100" key="genre-explorer" style={{ position: "relative", paddingBottom: "40px" }}>
                <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                    <ExportButton
                        context={this.getExportContext()}
                        enabled={!this.state.isLoading && !this.state.error && this.state.metricsData.length > 0}
                        scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                        onScheduleClick={() => this.setState({ showScheduleModal: true })}
                    />
                    <Button
                        onClick={this.toggleSidebar}
                        style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px" }}
                        title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                    >
                        <BookHalf />
                    </Button>
                </div>
                <Row>
                    <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                        <h2>Genre Explorer</h2>
                    </Col>
                </Row>

                <AnalyticsFiltersComponent
                    filters={filters}
                    filterOptions={filterOptions}
                    onFilterChange={this.handleFilterChange}
                    onApplyFilters={this.handleApplyFilters}
                    customerConfig={this.props.customerConfig}
                    enableGroupBy={{}}
                />
                <PopPeriodBanner startDate={appliedFilters.startDate} endDate={appliedFilters.endDate} />

                <Row style={{ marginTop: "8px", marginBottom: "20px" }}>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Genre</Form.Label>
                            <Form.Control
                                as="select"
                                value={selectedGenre}
                                onChange={this.handleGenreChange}
                                disabled={genres.length === 0 || isLoading || isGenresLoading}
                            >
                                {genres.length === 0 ? (
                                    <option value="">No genres available</option>
                                ) : (
                                    genres.map(genre => (
                                        <option key={genre} value={genre}>{genre}</option>
                                    ))
                                )}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                </Row>

                {isLoading ? (
                    <Row className="justify-content-center">
                        <Col className="text-center" style={{ padding: "32px" }}>
                            <Spinner animation="border" variant="primary" />
                        </Col>
                    </Row>
                ) : error ? (
                    <Row>
                        <Col>
                            <Alert variant="danger">{error}</Alert>
                        </Col>
                    </Row>
                ) : (
                    <>

                        <Row>
                            <Col>
                                <WidgetCard
                                    title="Genre Metrics"
                                    description={sidebarEntries.find(e => e.widgetKey === "content_genre_explorer_metrics")?.description}
                                    theme={theme}
                                    isEmpty={metricCards.length === 0}
                                    emptyMessage="No numeric metrics available to render as KPI widgets."
                                >
                                    <MetricCards
                                        metrics={metricCards}
                                        theme={theme}
                                        combineChangeAndDelta={true}
                                        noChangeLabel="—"
                                    />
                                </WidgetCard>
                            </Col>
                        </Row>

                        <Row style={{ marginTop: "20px" }}>
                            <Col>
                                <WidgetCard
                                    title={replaceAnalyticsBrandTerminology("Top Brands by Plays", this.props.customerConfig)}
                                    description={sidebarEntries.find(e => e.widgetKey === "content_genre_explorer_top_brands")?.description}
                                    theme={theme}
                                    isEmpty={topBrandsData.length === 0}
                                    emptyMessage={replaceAnalyticsBrandTerminology("No top brand data available for this genre and date range.", this.props.customerConfig)}
                                >
                                    <ResponsiveContainer width="100%" height={Math.max(560, topBrandsData.length * 28)}>
                                        <BarChart
                                            data={topBrandsData}
                                            layout="vertical"
                                            margin={{ top: 5, right: 24, left: 12, bottom: 5 }}
                                        >
                                            <CartesianGrid stroke="rgba(255,255,255,0.12)" strokeDasharray="3 3" />
                                            <XAxis
                                                type="number"
                                                tick={{ fill: "#ffffff" }}
                                                axisLine={{ stroke: "rgba(255,255,255,0.2)" }}
                                                tickLine={{ stroke: "rgba(255,255,255,0.2)" }}
                                                tickFormatter={(value: number) => value.toLocaleString()}
                                            />
                                            <YAxis
                                                type="category"
                                                dataKey="thingBrandName"
                                                width={280}
                                                interval={0}
                                                tick={{ fill: "#ffffff", fontSize: 12 }}
                                                axisLine={{ stroke: "rgba(255,255,255,0.2)" }}
                                                tickLine={{ stroke: "rgba(255,255,255,0.2)" }}
                                            />
                                            <Tooltip
                                                formatter={(value: number | string | undefined) => [Number(value ?? 0).toLocaleString(), "Plays"]}
                                                contentStyle={{
                                                    backgroundColor: "#1f1f1f",
                                                    border: "1px solid rgba(255,255,255,0.2)",
                                                    color: "#ffffff"
                                                }}
                                                labelStyle={{ color: "#ffffff" }}
                                                itemStyle={{ color: "#ffffff" }}
                                            />
                                            <Bar dataKey="plays">
                                                {topBrandsData.map((entry: TopBrandPoint, index: number) => (
                                                    <Cell
                                                        key={`brand-bar-${entry.thingBrandName}-${index}`}
                                                        fill={this.getBrandBarColor(index, topBrandsData.length)}
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </WidgetCard>
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
            </div>
            <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            <ScheduleExportModal
                show={this.state.showScheduleModal}
                onHide={() => this.setState({ showScheduleModal: false })}
                filters={this.state.appliedFilters}
                pageKey="content_genre_explorer"
                pageTitle="Genre Explorer"
                customerSlug={this.props.customerConfig.slug}
                customerConfig={this.props.customerConfig}
                editingSchedule={this.state.editingSchedule}
                onSave={this.handleScheduleSave}
            />
            </div>
        );
    }
}

export default ContentGenreExplorer;